import { SelectChangeEvent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RoutingProfile, TeamMember } from '@/types';
import { Toast } from '@/components/common/Toast';
import { api } from '@/store/api';
const { useUpdateUserRoutingProfileMutation, useGetRoutingProfilesQuery } = api;
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from '@gapartners/ui';
import { SelectField } from '@gapartners/ui/select';

type Props = {
  member: TeamMember;
  onClose: () => void;
};

export function TeamMemberQueuePopup({ member, onClose }: Props) {
  const { t } = useTranslation();
  const [updateUserRoutingProfile, { isLoading: isSaving }] =
    useUpdateUserRoutingProfileMutation();
  const {
    data: { routingProfiles = [] } = { routingProfiles: [] },
    isLoading,
  } = useGetRoutingProfilesQuery();
  const [selectedRoutingProfile, setSelectedRoutingProfile] =
    useState<RoutingProfile | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!routingProfiles.length) {
      setSelectedRoutingProfile(null);
      return;
    }

    const matchedRoutingProfile = routingProfiles.find(
      routingProfile => routingProfile.id === member.routingProfileId
    );
    setSelectedRoutingProfile(matchedRoutingProfile ?? null);
  }, [member, routingProfiles]);

  const handleSave = async () => {
    if (!selectedRoutingProfile || !member.connectId) return;

    setErrorMessage(null);
    try {
      await updateUserRoutingProfile({
        routingProfileId: selectedRoutingProfile.id,
        body: { userId: member.connectId },
      }).unwrap();
      onClose();
    } catch {
      setErrorMessage(t('errors.errorAssigningMemberToRoutingProfile'));
    }
  };

  if (isLoading) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="xs">
        <DialogContent
          sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
        >
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t('pages.manager.team.queueHeader')} {member.displayName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <SelectField
            label={t('pages.manager.team.selectQueue')}
            value={selectedRoutingProfile?.id ?? ''}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const val = e.target.value as string;
              const routingProfile =
                routingProfiles.find(rp => rp.id === val) ?? null;
              setSelectedRoutingProfile(routingProfile);
            }}
            fullWidth
            options={[
              { value: '', label: t('pages.manager.team.selectQueue') },
              ...routingProfiles.map(rp => ({
                value: rp.id,
                label: rp.name,
              })),
            ]}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || !selectedRoutingProfile}
          variant="contained"
          color="primary"
        >
          {t('common.save')}
        </Button>
      </DialogActions>

      {errorMessage && (
        <Toast
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
          type="error"
        />
      )}
    </Dialog>
  );
}
